<script setup lang="ts">
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useEventListener } from '@vueuse/core';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import VMenu from '../VMenu/VMenu.vue';
import { type NavItem } from './types';
import VNavItem from './VNavItem.vue';

export interface Props {
  items?: NavItem<string>[];
  activeNavItem?: string;
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  compact: false,
});

type NavItemWithActive = NavItem<string> & { active?: boolean };

const nav = ref();
const visibleNavItems = ref<NavItemWithActive[]>(props.items);
const more = ref<NavItemWithActive[]>([]);

watch(() => props.items, onResize);
watch(() => props.activeNavItem, onResize, { immediate: true });

function checkIfActiveItem(items: NavItemWithActive[]) {
  return items.some((item: NavItemWithActive) => item.active === true);
}

function onResize() {
  const temp: NavItemWithActive[] = props.items
    .slice()
    .map((t) => ({ ...t, active: t.key === props.activeNavItem }));
  let offSet = 1;
  visibleNavItems.value = props.items.slice();
  nextTick(() => {
    if (!nav.value) return;
    let availableWidthChildren = nav.value.getBoundingClientRect().width;
    let fittingElems = 0;

    for (let i = 0; i < nav.value.children.length; i++) {
      availableWidthChildren -= nav.value.children[i].getBoundingClientRect().width;
      if (availableWidthChildren >= 0) fittingElems++;
    }
    if (temp.length === fittingElems) offSet = 0;

    visibleNavItems.value = temp.splice(0, fittingElems - offSet);
    more.value = temp.splice(0);
  });
}
const menuRef = ref<InstanceType<typeof VMenu>>();
function toggle(event?: Event | undefined) {
  if (event) menuRef.value?.toggle(event);
}

const subMenuItems = computed(() =>
  more.value?.map((child) => ({
    label: child.label,
    icon: '',
    url: child.additionalFields,
  })),
);

useEventListener(window, 'resize', onResize);

onMounted(() => onResize());
</script>

<template>
  <div class="nav em-mt-4 em-mb-8 border-b border-b-(--borders)">
    <ul
      :class="[
        'nav-list -mb-px flex flex-wrap *:flex *:cursor-pointer *:items-center *:justify-center *:border-b-4 *:border-b-transparent *:hover:border-b-primary',
        {
          'compact gap-3': props.compact,
          'wide justify-between': !props.compact,
        },
      ]"
      ref="nav"
    >
      <li
        v-for="navItem in visibleNavItems"
        :class="{
          'active border-b-primary! font-semibold': navItem.active,
        }"
        :key="navItem.key"
      >
        <slot :default-component="VNavItem" :default-props="{ navItem }">
          <VNavItem :nav-item />
        </slot>
      </li>
      <li
        v-if="more.length"
        class="more relative cursor-pointer flex-col whitespace-nowrap"
        :class="{ active: checkIfActiveItem(more) }"
        @click="toggle"
      >
        <span>
          Overige
          <FontAwesomeIcon icon="fa-regular fa-chevron-down" />
        </span>

        <VMenu v-if="more.length" ref="menuRef" :model="subMenuItems" popup>
          <template #item="{ item, props }">
            <RouterLink v-slot="{ href, navigate }" :to="item.url!" custom>
              <a :href="href" v-bind="props.action" @click="navigate">
                <span>{{ item.label }}</span>
              </a>
            </RouterLink>
          </template>
        </VMenu>
      </li>
    </ul>
  </div>
</template>
